import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Image } from "../../../../components/common/image";
import {
  FLY_ANIMATION_MS,
  useWishlistUiStore,
  type FlyingWishlistItem,
} from "../../../../hooks/stores/useWishlistUiStore";

export function WishlistFlyLayer() {
  const flyingItems = useWishlistUiStore((s) => s.flyingItems);
  const anchor = useWishlistUiStore((s) => s.wishlistAnchor);

  if (typeof document === "undefined") return null;
  if (flyingItems.length === 0) return null;

  return createPortal(
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[60] overflow-visible"
    >
      {flyingItems.map((item) => (
        <FlyingImage key={item.id} item={item} anchor={anchor} />
      ))}
    </div>,
    document.body,
  );
}

function FlyingImage({
  item,
  anchor,
}: {
  item: FlyingWishlistItem;
  anchor: HTMLElement | null;
}) {
  const [phase, setPhase] = useState<"start" | "end">("start");

  useEffect(() => {
    const id = requestAnimationFrame(() => setPhase("end"));
    return () => cancelAnimationFrame(id);
  }, []);

  const { from } = item;
  const target = anchor?.getBoundingClientRect();
  const targetX = target ? target.left + target.width / 2 : from.x;
  const targetY = target ? target.top + target.height / 2 : from.y;

  const dx = targetX - (from.x + from.size / 2);
  const dy = targetY - (from.y + from.size / 2);

  const style: React.CSSProperties = {
    position: "fixed",
    left: from.x,
    top: from.y,
    width: from.size,
    height: from.size,
    transition: `transform ${FLY_ANIMATION_MS}ms cubic-bezier(0.55, 0.1, 0.85, 0.35), opacity ${FLY_ANIMATION_MS}ms ease-in`,
    transform:
      phase === "start"
        ? "translate3d(0,0,0) scale(1)"
        : `translate3d(${dx}px, ${dy}px, 0) scale(0.15)`,
    opacity: phase === "start" ? 1 : 0,
    willChange: "transform, opacity",
  };

  return (
    <div style={style} className="overflow-hidden rounded-2xl bg-bgWhite shadow-lg">
      <Image
        image={item.image}
        alt=""
        size="w-full h-full"
        showPlaceholder={false}
      />
    </div>
  );
}
